import type {
  SdkRecordScheduleTimeline,
  SdkRecordScheduleTimelinesHandle,
  SdkUseRecordScheduleTimelinesOptions
} from '@Pages/pageRuntime/sdk/types';
import type { IndexRecordScheduleTimelinesResult } from '@Workflows/api/RecordScheduleApi';
import {
  useRecordScheduleTimelines as useRecordScheduleTimelinesQuery,
  useSkipRecordScheduleOffset
} from '@Workflows/api/RecordScheduleApi';

import { usePollRefetchInterval } from './pollInterval';

/**
 * Reactive wrapper around the record-schedules endpoint backing
 * `Sdk.Components.RecordScheduleDisplay`, for pages that render a custom
 * schedule UI from the same data.
 *
 * Exposed via `Sdk.hooks.useRecordScheduleTimelines({ resourceId, recordId })`.
 *
 * A nullish `recordId` keeps the hook idle instead of throwing.
 */
export function useRecordScheduleTimelines({
  resourceId,
  recordId,
  recordScheduleWorkflowId,
  pollInterval
}: SdkUseRecordScheduleTimelinesOptions): SdkRecordScheduleTimelinesHandle {
  const schedule = usePollRefetchInterval<
    IndexRecordScheduleTimelinesResult,
    SdkRecordScheduleTimeline[]
  >(pollInterval, (data) => data?.data ?? []);

  const query = useRecordScheduleTimelinesQuery(
    resourceId,
    recordId ?? null,
    recordScheduleWorkflowId ?? null,
    schedule.refetchInterval === undefined ? {} : { refetchInterval: schedule.refetchInterval }
  );

  const skip = useSkipRecordScheduleOffset(resourceId, recordId ?? null);

  return {
    skipOffset: skip.mutateAsync,
    isSkipping: skip.isPending,
    timelines: query.data?.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch
  };
}
